import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import {
    getActiveFormUses,
    IngestionPointCategory,
    INGESTION_POINT_FORM_USES,
    IngestionPointFormUseKey,
} from "./data.ingestion.const";
import { DataIngestionService, IngestionPoint } from "./data.ingestion.service";
import { IngestionAccess } from "./ingestion.access";
import { IngestionFormTileComponent } from "./ingestion.form.tile.component";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";

const ALL_ORGANIZATIONS_FILTER = "__ALL_ORGANIZATIONS__";
const NO_ORGANIZATION_FILTER = "__NO_ORGANIZATION__";
const ALL_FORM_TYPES_FILTER = "__ALL_FORM_TYPES__";
const GENERAL_FORM_TYPE_FILTER = "__GENERAL_FORM_TYPE__";
const NO_ORGANIZATION_LABEL = "No organization";

type OrganizationFilterValue = typeof ALL_ORGANIZATIONS_FILTER | typeof NO_ORGANIZATION_FILTER | string;
type FormTypeFilterValue = typeof ALL_FORM_TYPES_FILTER | typeof GENERAL_FORM_TYPE_FILTER | IngestionPointFormUseKey;

/** Uses that list a form in My Forms; forms with no use at all are listed too. */
const MY_FORMS_USE_KEYS = ["eventCreation", "orgCreation"] as const satisfies readonly IngestionPointFormUseKey[];

function isMyFormsForm(form: IngestionPoint): boolean {
    const activeUses = getActiveFormUses(form.use);
    return !activeUses.length || MY_FORMS_USE_KEYS.some((useKey) => form.use?.[useKey]);
}

@Component({
    selector: "s25-ng-fillable-forms",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25LoadingInlineModule, IngestionFormTileComponent],
    template: `
        @if (formsResource.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (loadErrorMessage()) {
            <div class="empty-state">
                <h3>Unable to load forms</h3>
                <p>{{ loadErrorMessage() }}</p>
            </div>
        } @else if (!fillableForms().length) {
            <div class="empty-state">
                <h3>No fillable forms</h3>
                <p>There are no active forms available right now.</p>
            </div>
        } @else {
            <div class="filters-container">
                <div class="filter-group">
                    <label class="filter-label" for="fillable-forms-organization-filter">Organization</label>
                    <select
                        id="fillable-forms-organization-filter"
                        class="filter-control"
                        [value]="selectedOrganizationFilter()"
                        (change)="onOrganizationFilterChange($event)"
                    >
                        @for (organizationOption of organizationOptions(); track organizationOption.value) {
                            <option [value]="organizationOption.value">{{ organizationOption.label }}</option>
                        }
                    </select>
                </div>

                <div class="filter-group">
                    <label class="filter-label" for="fillable-forms-type-filter">Form Type</label>
                    <select
                        id="fillable-forms-type-filter"
                        class="filter-control"
                        [value]="selectedFormTypeFilter()"
                        (change)="onFormTypeFilterChange($event)"
                    >
                        @for (formTypeOption of formTypeOptions(); track formTypeOption.value) {
                            <option [value]="formTypeOption.value">{{ formTypeOption.label }}</option>
                        }
                    </select>
                </div>
            </div>

            @if (!filteredForms().length) {
                <div class="empty-state">
                    <h3>No forms match</h3>
                    <p>No forms match the selected organization and form type filters.</p>
                </div>
            } @else {
                <div class="forms-grid">
                    @for (form of filteredForms(); track form.ingestionPointId) {
                        <s25-ng-ingestion-form-tile
                            [form]="form"
                            [showOrganization]="true"
                            (formStart)="startForm.emit($event)"
                        />
                    }
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .filters-container {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 1rem;
        }

        .filter-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 220px;
        }

        .filter-label {
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            margin: 0;
        }

        .filter-control {
            padding: 0.5rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 0.9rem;
            background-color: white;
        }

        .filter-control:focus {
            outline: none;
            border-color: #2573a7;
            box-shadow: 0 0 0 2px rgba(37, 115, 167, 0.1);
        }

        .forms-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1rem;
        }

        .empty-state {
            text-align: center;
            padding: 2rem 0;
            color: #666;
        }

        .empty-state h3 {
            margin: 0 0 0.75rem;
        }

        .empty-state p {
            margin: 0;
        }

        @media (max-width: 768px) {
            .filter-group {
                flex: 1;
            }
        }
    `,
})
export class FillableFormsComponent {
    readonly category = input(IngestionPointCategory.ENGAGEMENT);
    readonly startForm = output<number>();

    readonly selectedOrganizationFilter = signal<OrganizationFilterValue>(ALL_ORGANIZATIONS_FILTER);
    readonly selectedFormTypeFilter = signal<FormTypeFilterValue>(ALL_FORM_TYPES_FILTER);

    readonly formsResource = s25Resource({
        params: () => this.category(),
        loader: async ({ params: category }) => {
            const fillableForms = await DataIngestionService.getIngestionPoints(
                category,
                undefined,
                undefined,
                undefined,
                IngestionAccess.IngestionPointAction.FILL,
            );
            const activeForms = fillableForms.filter(
                (fillableForm) => fillableForm.enabled && fillableForm.active !== false,
            );
            await DataIngestionService.populateSubCategoryNames(category, activeForms);
            return activeForms;
        },
        defaultValue: [],
    });

    readonly fillableForms = computed(() => this.formsResource.value()?.filter(isMyFormsForm) ?? []);

    readonly loadErrorMessage = computed(() => {
        const resourceError = this.formsResource.error();
        return resourceError
            ? S25Util.errorText(resourceError) || "Something went wrong while loading forms."
            : undefined;
    });

    readonly organizationOptions = computed(() => {
        const labelsByOrganizationValue = new Map<string, string>();
        let hasFormsWithoutOrganization = false;

        for (const fillableForm of this.fillableForms()) {
            if (!fillableForm.subCat?.trim()) {
                hasFormsWithoutOrganization = true;
                continue;
            }
            labelsByOrganizationValue.set(fillableForm.subCat, fillableForm.subCategoryName || fillableForm.subCat);
        }

        const organizationOptions = [
            { value: ALL_ORGANIZATIONS_FILTER, label: "All organizations" },
            ...Array.from(labelsByOrganizationValue.entries())
                .sort((firstEntry, secondEntry) => firstEntry[1].localeCompare(secondEntry[1]))
                .map(([value, label]) => ({ value, label })),
        ];

        if (hasFormsWithoutOrganization) {
            organizationOptions.push({ value: NO_ORGANIZATION_FILTER, label: NO_ORGANIZATION_LABEL });
        }

        return organizationOptions;
    });

    readonly formTypeOptions = computed(() => {
        const availableFormTypes = new Set<FormTypeFilterValue>([ALL_FORM_TYPES_FILTER]);
        let hasGeneralForms = false;

        for (const fillableForm of this.fillableForms()) {
            const matchingUseTypes = getActiveFormUses(fillableForm.use);
            if (!matchingUseTypes.length) {
                hasGeneralForms = true;
                continue;
            }
            for (const formUseKey of matchingUseTypes) {
                availableFormTypes.add(formUseKey);
            }
        }

        const typeOptions = [{ value: ALL_FORM_TYPES_FILTER, label: "All form types" }];
        if (hasGeneralForms) {
            typeOptions.push({ value: GENERAL_FORM_TYPE_FILTER, label: "General" });
        }
        for (const formUseKey of MY_FORMS_USE_KEYS) {
            if (availableFormTypes.has(formUseKey)) {
                typeOptions.push({ value: formUseKey, label: INGESTION_POINT_FORM_USES[formUseKey] });
            }
        }
        return typeOptions;
    });

    readonly filteredForms = computed(() =>
        this.fillableForms().filter((fillableForm) => {
            return this.matchesOrganizationFilter(fillableForm) && this.matchesFormTypeFilter(fillableForm);
        }),
    );

    onOrganizationFilterChange(event: Event): void {
        const selectElement = event.target as HTMLSelectElement;
        this.selectedOrganizationFilter.set(selectElement.value as OrganizationFilterValue);
    }

    onFormTypeFilterChange(event: Event): void {
        const selectElement = event.target as HTMLSelectElement;
        this.selectedFormTypeFilter.set(selectElement.value as FormTypeFilterValue);
    }

    private matchesOrganizationFilter(fillableForm: IngestionPoint): boolean {
        const selectedOrganizationValue = this.selectedOrganizationFilter();
        if (selectedOrganizationValue === ALL_ORGANIZATIONS_FILTER) {
            return true;
        }
        if (selectedOrganizationValue === NO_ORGANIZATION_FILTER) {
            return !fillableForm.subCat?.trim();
        }
        return fillableForm.subCat === selectedOrganizationValue;
    }

    private matchesFormTypeFilter(fillableForm: IngestionPoint): boolean {
        const selectedFormType = this.selectedFormTypeFilter();
        if (selectedFormType === ALL_FORM_TYPES_FILTER) {
            return true;
        }
        if (selectedFormType === GENERAL_FORM_TYPE_FILTER) {
            return !this.hasNamedFormUse(fillableForm);
        }
        return !!fillableForm.use?.[selectedFormType];
    }

    private hasNamedFormUse(fillableForm: IngestionPoint): boolean {
        return getActiveFormUses(fillableForm.use).length > 0;
    }
}
